import { SlidersHorizontal } from 'lucide-react';
import { useRef, useState } from 'react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

import { type ConversationsInboxSortKey } from './conversations-inbox-controller';

const ORDER_LABELS: Readonly<Record<ConversationsInboxSortKey, string>> = {
    newest: 'Newest messages',
    oldest: 'Oldest messages',
};

/**
 * Order by, split from Filter the way Linear does it: filters decide which
 * rows exist, this menu decides how the surviving rows are arranged. The
 * list has one axis (last activity), so the menu is one radio pair.
 *
 * Open state is controlled by the toolbar so it can never overlap Filter
 * (see useExclusiveToolbarMenus).
 */
export function ConversationInboxViewOptions({
    sort,
    onSortChange,
    open,
    onOpenChange,
    className,
}: {
    readonly sort: ConversationsInboxSortKey;
    readonly onSortChange: (sort: ConversationsInboxSortKey) => void;
    readonly open?: boolean;
    readonly onOpenChange?: (open: boolean) => void;
    readonly className?: string;
}) {
    const hoveringRef = useRef(false);
    // The menu hands focus back to its trigger when it closes, and a tooltip
    // also opens on focus, so "Order by" popped up after every choice and
    // every click-away. That returned focus is suppressed until the trigger
    // blurs; hover and a fresh keyboard focus still show the tooltip.
    const suppressFocusTooltipRef = useRef(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    function handleTooltipOpenChange(next: boolean) {
        if (next && !hoveringRef.current && suppressFocusTooltipRef.current) {
            return;
        }
        setTooltipOpen(next);
    }
    return (
        <DropdownMenu
            onOpenChange={(next) => {
                // Opening the menu hides the tooltip, but a controlled
                // tooltip is not told about a pointer-leave while hidden,
                // so drop its own state here or it reappears on close.
                if (next) {
                    setTooltipOpen(false);
                }
                onOpenChange?.(next);
            }}
            open={open}
        >
            <Tooltip
                onOpenChange={handleTooltipOpenChange}
                open={tooltipOpen && open !== true}
            >
                <TooltipTrigger asChild={true}>
                    <DropdownMenuTrigger asChild={true}>
                        <button
                            aria-label="Order by"
                            className={cn(
                                'text-foreground focus-visible:ring-ring/60 data-[state=open]:bg-accent flex h-6 shrink-0 items-center rounded-full px-2.5 outline-none hover:bg-black/5 focus-visible:ring-2 dark:hover:bg-white/10',
                                className,
                            )}
                            data-testid="conversations-inbox-order-by"
                            onPointerEnter={() => {
                                hoveringRef.current = true;
                            }}
                            onBlur={() => {
                                suppressFocusTooltipRef.current = false;
                            }}
                            onPointerLeave={() => {
                                hoveringRef.current = false;
                            }}
                            type="button"
                        >
                            <SlidersHorizontal className="size-3.5" />
                        </button>
                    </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Order by</TooltipContent>
            </Tooltip>
            <DropdownMenuContent
                align="end"
                className="w-48"
                onCloseAutoFocus={() => {
                    suppressFocusTooltipRef.current = true;
                }}
            >
                <DropdownMenuLabel className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                    Order by
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                    onValueChange={(value) => {
                        if (value === 'newest' || value === 'oldest') {
                            onSortChange(value);
                        }
                    }}
                    value={sort}
                >
                    {(
                        Object.keys(
                            ORDER_LABELS,
                        ) as Array<ConversationsInboxSortKey>
                    ).map((key) => (
                        <DropdownMenuRadioItem key={key} value={key}>
                            {ORDER_LABELS[key]}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
